import React, { useState, useEffect } from 'react';
import { Users, Briefcase, Wallet, TrendingUp, Receipt, DollarSign, Loader, ShieldAlert, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import TripjackBalanceCard from '../../components/admin/TripjackBalanceCard';
import RiyaBalanceCard from '../../components/admin/RiyaBalanceCard';
import RiyaHealthCard from '../../components/admin/RiyaHealthCard';
import RiyaProxyHealthCard from '../../components/admin/RiyaProxyHealthCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gstReadiness, setGstReadiness] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchGstReadiness();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGstReadiness = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/settings/gst/readiness`, {
        withCredentials: true,
      });
      setGstReadiness(r.data.readiness);
    } catch (e) {
      // Non-blocking — if this errors (e.g. first login), just hide the banner
      setGstReadiness(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">Failed to load dashboard stats</div>
      </div>
    );
  }

  const statsCards = [
    { 
      label: 'Total B2C Users', 
      value: stats.total_users || 0, 
      icon: Users, 
      color: 'blue',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    { 
      label: 'Active Agents', 
      value: stats.total_agents || 0, 
      icon: Briefcase, 
      color: 'purple',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600'
    },
    { 
      label: 'Total Bookings', 
      value: stats.total_bookings || 0, 
      icon: Receipt, 
      color: 'green',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    { 
      label: 'Confirmed Bookings', 
      value: stats.confirmed_bookings || 0, 
      icon: Receipt, 
      color: 'teal',
      bgColor: 'bg-teal-50',
      iconColor: 'text-teal-600'
    },
    { 
      label: 'GMV (Confirmed)', 
      value: `₹${(stats.total_gmv ?? stats.total_revenue ?? 0).toLocaleString('en-IN')}`, 
      icon: DollarSign, 
      color: 'orange',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600'
    },
    { 
      label: 'Net Revenue (All-time)', 
      value: `₹${(stats.total_net_revenue ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`, 
      icon: TrendingUp, 
      color: 'teal',
      bgColor: 'bg-teal-50',
      iconColor: 'text-teal-600',
      subText: 'Markup + ACF retained commission',
    },
    { 
      label: 'Wallet Balance', 
      value: `₹${(stats.total_wallet_balance || 0).toLocaleString('en-IN')}`, 
      icon: Wallet, 
      color: 'green',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    { 
      label: 'Pending Topup Requests', 
      value: stats.pending_topup_requests || 0, 
      icon: TrendingUp, 
      color: 'red',
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600'
    },
    { 
      label: 'Airline Commission (MTD)', 
      value: `₹${(stats.airline_commission_mtd || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
      icon: TrendingUp,
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      subText: `ACF retained: ₹${(stats.acf_commission_retained_mtd ?? stats.airline_commission_mtd ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} · Agent shared: ₹${(stats.agent_commission_shared_mtd || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
    },
    { 
      label: 'Commission GST Liability (MTD)', 
      value: `₹${(stats.gst_on_commission_mtd || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
      icon: Receipt,
      color: 'amber',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
      subText: '18% GST on ACF retained commission only',
    },
    { 
      label: 'ACF Net Revenue (MTD)', 
      value: `₹${(stats.gross_margin_mtd || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: 'indigo',
      bgColor: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      subText: `Markup ₹${(stats.markup_income_mtd || 0).toLocaleString('en-IN')} + ACF retained commission`,
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
        <p className="text-gray-500">Welcome back, Admin! Here's what's happening today.</p>
      </div>

      {/* GST readiness nudge — visible only until Settings → GST is complete */}
      {gstReadiness && gstReadiness.ready === false && (
        <Link
          to="/admin/compliance/settings"
          data-testid="admin-gst-config-banner"
          className="mb-8 block rounded-xl border border-red-200 bg-gradient-to-r from-red-50 to-amber-50 p-5 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-red-900">
                Complete GST setup to enable invoicing
              </h3>
              <p className="text-sm text-red-800 mt-1">
                Company GSTIN, PAN, and registered address are required before any Tax Invoice,
                Credit Note, or Supplementary Invoice can be generated. Bookings are working
                normally, but invoicing is paused.
              </p>
              {(gstReadiness.missing?.length > 0 || gstReadiness.invalid?.length > 0) && (
                <p className="text-xs text-red-700 mt-2">
                  <span className="font-semibold">Pending:</span>{' '}
                  {[...(gstReadiness.missing || []), ...(gstReadiness.invalid || [])].join(' · ')}
                </p>
              )}
            </div>
            <div className="hidden md:flex items-center gap-1 text-sm font-semibold text-red-900">
              Configure now <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statsCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                </div>
              </div>
              <h3 className="text-gray-500 text-sm font-medium mb-1">{stat.label}</h3>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              {stat.subText && (
                <p className="text-xs text-gray-400 mt-1">{stat.subText}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Supplier widgets — dedicated row so all 4 stay side-by-side regardless of stat count */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        {/* Sprint T4 — Tripjack supplier wallet balance */}
        <TripjackBalanceCard />
        {/* Sprint B/R5 — Riya supplier wallet balance */}
        <RiyaBalanceCard />
        {/* Sprint C+ — Riya health check */}
        <RiyaHealthCard />
        {/* Phase A++ — Riya outbound proxy health (Bright Data) */}
        <RiyaProxyHealthCard />
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h2>
        <div className="space-y-4">
          {[
            { type: 'user', message: 'New user registered: john@example.com', time: '2 mins ago' },
            { type: 'booking', message: 'Booking confirmed: BOM → DXB', time: '15 mins ago' },
            { type: 'wallet', message: 'Agent wallet topup request: ₹50,000', time: '1 hour ago' },
            { type: 'markup', message: 'Markup updated for Air India routes', time: '2 hours ago' },
          ].map((activity, idx) => (
            <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                activity.type === 'user' ? 'bg-blue-100 text-blue-600' :
                activity.type === 'booking' ? 'bg-green-100 text-green-600' :
                activity.type === 'wallet' ? 'bg-orange-100 text-orange-600' :
                'bg-purple-100 text-purple-600'
              }`}>
                {activity.type === 'user' && <Users className="w-5 h-5" />}
                {activity.type === 'booking' && <Receipt className="w-5 h-5" />}
                {activity.type === 'wallet' && <Wallet className="w-5 h-5" />}
                {activity.type === 'markup' && <TrendingUp className="w-5 h-5" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                <p className="text-xs text-gray-500">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
